import React, { useMemo } from 'react';
import { useTranslation } from 'localization';
import type { ApproachOrbitConfig } from 'types/approach';
import type {
  KeyDateDefinition,
  RRuleSchedule,
  ReferenceSchedule,
  ScheduleDefinition,
} from 'types/orbit';
import TagAtom, { TagTone } from '../../atoms/Tag/TagAtom';
import './OrbitScheduleMolecule.scss';

export interface OrbitScheduleMoleculeProps {
  orbit: ApproachOrbitConfig;
  className?: string;
}

interface NamedSchedule {
  key: string;
  schedule: ScheduleDefinition;
}

const isReference = (s: ScheduleDefinition): s is ReferenceSchedule =>
  s.type === 'reference';
const isRRuleLike = (s: ScheduleDefinition): s is RRuleSchedule =>
  s.type === 'rrule' || s.type === 'cron';

const grammarLabelKey = (s: ScheduleDefinition): string =>
  isReference(s)
    ? 'marketplace.approachDetail.whatsIncluded.orbit.grammar.referenced'
    : 'marketplace.approachDetail.whatsIncluded.orbit.grammar.schedule';

const grammarVariant = (s: ScheduleDefinition): TagTone =>
  isReference(s) ? 'accent' : 'info';

const englishForSchedule = (
  s: ScheduleDefinition,
  t: (key: string, params?: Record<string, unknown>) => string,
): string => {
  if (isRRuleLike(s)) {
    if (s.type === 'rrule' && s.rrule) return s.rrule;
    if (s.type === 'cron' && s.cron) return s.cron;
    return t('marketplace.approachDetail.whatsIncluded.orbit.scheduleNoSpec');
  }
  // reference
  const label =
    s.timeframeSystemName ||
    s.targetTimeframeSchema ||
    s.timeframeQuery ||
    s.timeframeId;
  if (label) {
    return t(
      'marketplace.approachDetail.whatsIncluded.orbit.referenceTimeframe',
      { name: label },
    );
  }
  return t('marketplace.approachDetail.whatsIncluded.orbit.referenceUnnamed');
};

const ownsKeyDate = (
  schedule: ScheduleDefinition,
  keyDate: KeyDateDefinition,
): boolean => {
  const anchors = [schedule.startAnchorKey, schedule.endAnchorKey].filter(
    Boolean,
  ) as string[];
  return anchors.includes(keyDate.name);
};

const OrbitScheduleMolecule: React.FC<OrbitScheduleMoleculeProps> = ({
  orbit,
  className = '',
}) => {
  const { t } = useTranslation();

  const schedules: NamedSchedule[] = useMemo(() => {
    const inline = orbit.inlineDefinition?.schedules;
    if (!inline) return [];
    return Object.entries(inline).map(([key, schedule]) => ({ key, schedule }));
  }, [orbit.inlineDefinition]);

  const allKeyDates: KeyDateDefinition[] = useMemo(
    () => orbit.inlineDefinition?.keyDates ?? [],
    [orbit.inlineDefinition],
  );

  const { datesBySchedule, orphanDates } = useMemo(() => {
    const owned: Record<string, KeyDateDefinition[]> = {};
    const orphans: KeyDateDefinition[] = [];
    for (const kd of allKeyDates) {
      const owner = schedules.find(({ schedule }) => ownsKeyDate(schedule, kd));
      if (owner) {
        (owned[owner.key] ||= []).push(kd);
      } else {
        orphans.push(kd);
      }
    }
    return { datesBySchedule: owned, orphanDates: orphans };
  }, [allKeyDates, schedules]);

  const sourceLabel =
    orbit.source === 'template'
      ? t('marketplace.approachDetail.whatsIncluded.orbit.sourceTemplate', {
          name:
            orbit.templateSystemName ||
            t('marketplace.approachDetail.whatsIncluded.orbit.templateUnnamed'),
        })
      : t('marketplace.approachDetail.whatsIncluded.orbit.sourceInline');

  const rootClasses = ['marketplace-orbit-schedule', className]
    .filter(Boolean)
    .join(' ');

  const hasContent = schedules.length > 0 || orphanDates.length > 0;

  return (
    <section className={rootClasses}>
      <header className="marketplace-orbit-schedule__header">
        <TagAtom tone="outline" label={sourceLabel} />
        {orbit.startDate && (
          <TagAtom
            tone="neutral"
            label={t(
              'marketplace.approachDetail.whatsIncluded.orbit.startDate',
              { date: orbit.startDate },
            )}
          />
        )}
        {orbit.endDate && (
          <TagAtom
            tone="neutral"
            label={t(
              'marketplace.approachDetail.whatsIncluded.orbit.endDate',
              { date: orbit.endDate },
            )}
          />
        )}
      </header>

      {!hasContent && (
        <p className="marketplace-orbit-schedule__empty">
          {orbit.source === 'template'
            ? t(
                'marketplace.approachDetail.whatsIncluded.orbit.templateOnly',
              )
            : t(
                'marketplace.approachDetail.whatsIncluded.orbit.noSchedules',
              )}
        </p>
      )}

      {schedules.length > 0 && (
        <ul className="marketplace-orbit-schedule__list">
          {schedules.map(({ key, schedule }) => {
            const owned = datesBySchedule[key] ?? [];
            const tz = isRRuleLike(schedule) ? schedule.timezone : undefined;
            return (
              <li key={key} className="marketplace-orbit-schedule__schedule">
                <div className="marketplace-orbit-schedule__schedule-header">
                  <i
                    className="pi pi-clock marketplace-orbit-schedule__schedule-icon"
                    aria-hidden="true"
                  />
                  <span className="marketplace-orbit-schedule__schedule-name">
                    {key}
                  </span>
                  <TagAtom
                    tone={grammarVariant(schedule)}
                    label={t(grammarLabelKey(schedule))}
                  />
                  {tz && <TagAtom tone="outline" label={tz} />}
                </div>
                <p className="marketplace-orbit-schedule__schedule-english">
                  {englishForSchedule(schedule, t)}
                </p>
                {owned.length > 0 && (
                  <div className="marketplace-orbit-schedule__key-dates">
                    <div className="marketplace-orbit-schedule__key-dates-label">
                      {t(
                        'marketplace.approachDetail.whatsIncluded.orbit.keyDatesHeading',
                      )}
                    </div>
                    <ul className="marketplace-orbit-schedule__key-date-list">
                      {owned.map((kd) => (
                        <li
                          key={kd.name}
                          className="marketplace-orbit-schedule__key-date"
                        >
                          <i
                            className="pi pi-calendar marketplace-orbit-schedule__key-date-icon"
                            aria-hidden="true"
                          />
                          <b className="marketplace-orbit-schedule__key-date-name">
                            {kd.localizationKey ? t(kd.localizationKey) : kd.name}
                          </b>
                          <span className="marketplace-orbit-schedule__key-date-source">
                            {t(
                              `marketplace.approachDetail.whatsIncluded.orbit.keyDateSource.${kd.source}`,
                            )}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {orphanDates.length > 0 && (
        <div className="marketplace-orbit-schedule__orphans">
          <div className="marketplace-orbit-schedule__orphans-header">
            <i
              className="pi pi-calendar marketplace-orbit-schedule__orphans-icon"
              aria-hidden="true"
            />
            <span className="marketplace-orbit-schedule__orphans-title">
              {t(
                'marketplace.approachDetail.whatsIncluded.orbit.standaloneKeyDates',
              )}
            </span>
          </div>
          <p className="marketplace-orbit-schedule__orphans-help">
            {t(
              'marketplace.approachDetail.whatsIncluded.orbit.standaloneKeyDatesHelp',
            )}
          </p>
          <ul className="marketplace-orbit-schedule__key-date-list">
            {orphanDates.map((kd) => (
              <li key={kd.name} className="marketplace-orbit-schedule__key-date">
                <i
                  className="pi pi-calendar marketplace-orbit-schedule__key-date-icon"
                  aria-hidden="true"
                />
                <b className="marketplace-orbit-schedule__key-date-name">
                  {kd.localizationKey ? t(kd.localizationKey) : kd.name}
                </b>
                <span className="marketplace-orbit-schedule__key-date-source">
                  {t(
                    `marketplace.approachDetail.whatsIncluded.orbit.keyDateSource.${kd.source}`,
                  )}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};

OrbitScheduleMolecule.displayName = 'OrbitScheduleMolecule';

export default OrbitScheduleMolecule;
